import { FC, useState, useEffect, useRef } from "react"
import {
  useFindConnectorLogsQuery,
  useLazyFetchLeadDetailsQuery,
  useLazyFetchSubscriptionDetailsQuery
} from "../../../../../../redux/queries"
import { formatDateTimeFromTimestamp } from "../../../../../utils"

interface Props {
  connectorId: string
}

const POLL_INTERVAL = 5000 // 5 seconds
const INACTIVITY_TIMEOUT = 60000 // 1 minute
const LOGS_PER_PAGE = 25

const ConnectorLogs: FC<Props> = ({ connectorId }) => {
  const [expandedLogId, setExpandedLogId] = useState<string>("")
  const [recordDetailsMap, setRecordDetailsMap] = useState<Record<string, any>>(
    {}
  )
  const [isPolling, setIsPolling] = useState(true)
  const [offset, setOffset] = useState(0)
  const [allLogs, setAllLogs] = useState<any[]>([])
  const lastActivityRef = useRef<number>(Date.now())
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const {
    data: logsData,
    refetch,
    isFetching
  } = useFindConnectorLogsQuery(
    { connector_id: connectorId, limit: LOGS_PER_PAGE, offset },
    { skip: !connectorId }
  )

  const [fetchLeadDetails, { isLoading: isFetchingLead }] =
    useLazyFetchLeadDetailsQuery()
  const [fetchSubscriptionDetails, { isLoading: isFetchingSubscription }] =
    useLazyFetchSubscriptionDetailsQuery()

  const isFetchingRecord = isFetchingLead || isFetchingSubscription

  // Accumulate logs as we load more
  useEffect(() => {
    if (logsData?.logs) {
      if (offset === 0) {
        // First page, replace all logs
        setAllLogs(logsData.logs)
      } else {
        // Append new logs, avoiding duplicates
        setAllLogs(prev => {
          const existingIds = new Set(prev.map(l => l.id))
          const newLogs = logsData.logs.filter(l => !existingIds.has(l.id))
          return [...prev, ...newLogs]
        })
      }
    }
  }, [logsData, offset])

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (
          entries[0].isIntersecting &&
          !isFetching &&
          logsData &&
          allLogs.length < logsData.count
        ) {
          // Load more logs
          setOffset(prev => prev + LOGS_PER_PAGE)
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [isFetching, logsData, allLogs.length])

  // Reset offset when polling refreshes
  const handleRefresh = () => {
    setOffset(0)
    updateActivity()
    refetch()
  }

  // Update last activity time on any user interaction
  const updateActivity = () => {
    lastActivityRef.current = Date.now()
    if (!isPolling) {
      setIsPolling(true)
    }
  }

  // Auto-polling with inactivity detection
  useEffect(() => {
    if (!connectorId || !isPolling) return

    const pollInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current

      if (timeSinceActivity > INACTIVITY_TIMEOUT) {
        // Stop polling after inactivity
        setIsPolling(false)
        console.log("Stopped polling logs due to inactivity")
      } else {
        refetch()
      }
    }, POLL_INTERVAL)

    return () => clearInterval(pollInterval)
  }, [connectorId, isPolling, refetch])

  // Track user activity
  useEffect(() => {
    const handleActivity = () => updateActivity()

    window.addEventListener("mousemove", handleActivity)
    window.addEventListener("keydown", handleActivity)
    window.addEventListener("click", handleActivity)

    return () => {
      window.removeEventListener("mousemove", handleActivity)
      window.removeEventListener("keydown", handleActivity)
      window.removeEventListener("click", handleActivity)
    }
  }, [])

  const handleViewRecord = async (
    recordId: string,
    recordType: string,
    logId: string
  ) => {
    // If already loaded, toggle visibility
    if (recordDetailsMap[logId]) {
      const newMap = { ...recordDetailsMap }
      delete newMap[logId]
      setRecordDetailsMap(newMap)
      return
    }

    // Fetch record details
    try {
      let result: any
      if (recordType === "lead") {
        result = await fetchLeadDetails(recordId).unwrap()
      } else if (
        recordType === "subscription" ||
        recordType === "pending_subscription"
      ) {
        result = await fetchSubscriptionDetails({ id: recordId }).unwrap()
      }

      if (result) {
        setRecordDetailsMap(prev => ({ ...prev, [logId]: result }))
      }
    } catch (error) {
      console.error("Failed to fetch record details:", error)
      setRecordDetailsMap(prev => ({
        ...prev,
        [logId]: { error: "Failed to load record details" }
      }))
    }
  }

  if (!connectorId) {
    return (
      <div className="text-center py-8 text-gray-500">
        Save the connector first to view logs
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-700">Activity Log</h3>
          <p className="text-xs text-gray-500 mt-1">
            {logsData?.count || 0} total events
            {isPolling && (
              <span className="ml-2 text-green-600">● Auto-refreshing</span>
            )}
            {!isPolling && (
              <span className="ml-2 text-gray-400">○ Paused (inactive)</span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {isFetching ? "Refreshing..." : "Refresh"}
          </button>
          <button
            type="button"
            onClick={() => setIsPolling(!isPolling)}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 font-medium"
          >
            {isPolling ? "Pause" : "Resume"}
          </button>
        </div>
      </div>

      {allLogs.length > 0 ? (
        <div className="border border-gray-200 rounded-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Direction
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Record ID
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Date/Time
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allLogs.map(log => (
                <>
                  <tr
                    key={log.id}
                    className={
                      expandedLogId === log.id
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    }
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`text-sm ${
                          log.status === "success"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {log.status === "success" ? "✓" : "✗"}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                      {log.direction === "incoming" ? "← In" : "→ Out"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                      {log.record_type}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                      {log.record_id || "-"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                      {formatDateTimeFromTimestamp(
                        log.timestamp,
                        "LL/dd/yyyy h:mm:ss a"
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedLogId(
                            expandedLogId === log.id ? "" : log.id
                          )
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {expandedLogId === log.id ? "▼" : "▶"}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded details row */}
                  {expandedLogId === log.id && (
                    <tr>
                      <td colSpan={6} className="px-3 py-3 bg-gray-50">
                        <div className="space-y-2">
                          {log.error_message && (
                            <div className="text-sm text-red-600 mb-2">
                              <strong>Error:</strong> {log.error_message}
                            </div>
                          )}

                          <details className="text-xs">
                            <summary className="cursor-pointer font-medium mb-1">
                              Request Data
                            </summary>
                            <pre className="mt-2 bg-white p-2 rounded overflow-x-auto max-h-48 text-xs whitespace-pre-wrap break-words">
                              {JSON.stringify(log.request_data, null, 2)}
                            </pre>
                          </details>

                          {log.status === "success" && log.record_id && (
                            <div>
                              <button
                                type="button"
                                onClick={() =>
                                  handleViewRecord(
                                    log.record_id,
                                    log.record_type,
                                    log.id
                                  )
                                }
                                className="text-blue-600 hover:text-blue-800 font-medium text-xs"
                                disabled={isFetchingRecord}
                              >
                                {isFetchingRecord && !recordDetailsMap[log.id]
                                  ? "Loading..."
                                  : recordDetailsMap[log.id]
                                  ? "Hide"
                                  : "Load"}{" "}
                                {log.record_type} Details
                              </button>

                              {recordDetailsMap[log.id] && (
                                <pre className="mt-2 bg-white p-2 rounded overflow-x-auto max-h-64 border border-gray-200 text-xs whitespace-pre-wrap break-words">
                                  {JSON.stringify(
                                    recordDetailsMap[log.id],
                                    null,
                                    2
                                  )}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          {/* Infinite scroll trigger */}
          {logsData && allLogs.length < logsData.count && (
            <div
              ref={loadMoreRef}
              className="py-4 text-center text-sm text-gray-500"
            >
              {isFetching ? "Loading more logs..." : "Scroll for more"}
            </div>
          )}

          {logsData && allLogs.length >= logsData.count && (
            <div className="py-4 text-center text-sm text-gray-400">
              End of log history
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {isFetching ? "Loading logs..." : "No activity logs yet"}
        </div>
      )}
    </div>
  )
}

export default ConnectorLogs
